import { forwardRef } from 'react'
import Link from 'next/link'
import { cn } from '@scalis/utils'
import { ButtonAlign, ButtonKind, ButtonProps, ButtonStandaloneKind, ButtonWidth } from './button.types'
import {
  standaloneButtonClassName,
  buttonClassName,
  getStandaloneButtonHoverElementStyles,
  getButtonHoverElementStyles,
} from './styles'

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      text,
      onClick,
      disabled,
      align = ButtonAlign.center,
      size,
      width,
      children,
      link,
      asChild,
      loading = false,
      startAdornment,
      endAdornment,
      isStandalone = false,
      kind,
      className,
      noWrap = true,
      rounded = true,
      ...props
    },
    ref,
  ) => {
    const allClassNames = isStandalone
      ? standaloneButtonClassName({
          kind: kind as ButtonStandaloneKind,
          size,
          width,
          rounded,
        })
      : buttonClassName({ kind: kind as ButtonKind, size, width, rounded })

    // Get the hover background color directly based on button kind

    const elementProps = {
      className: cn(
        allClassNames,
        'group',
        'isolate relative',
        'overflow-hidden',
        'transition-colors duration-300',
        className,
      ),
      onClick,
      disabled: disabled || loading,
      'aria-label': props['aria-label'] ?? props.title,
    }
    const content = (
      <>
        <div
          aria-hidden='true'
          className={cn(
            'group-active:opacity-0',
            'absolute inset-0 opacity-100',
            'transform translate-y-full transition-transform duration-300 ease-out',
            'pointer-events-none',
            !disabled && !loading && 'group-hover:translate-y-0',
            isStandalone && getStandaloneButtonHoverElementStyles(kind as ButtonStandaloneKind),
            !isStandalone && getButtonHoverElementStyles(kind as ButtonKind),
            'group-active:bg-opacity-0',
          )}
        />
        <div
          className={cn('relative flex w-full items-center gap-2 z-10', {
            'justify-center text-center': align === ButtonAlign.center || width === ButtonWidth.full,
            'justify-start text-start': align === ButtonAlign.left,
            'justify-end text-end': align === ButtonAlign.right,
            'whitespace-nowrap': noWrap,
          })}
        >
          {startAdornment && (
            <i
              aria-hidden
              aria-label='startAdornment'
              className={cn(startAdornment, 'fa-1x')}
            />
          )}
          <>
            <span className={loading ? 'invisible' : ''}>{text ?? children}</span>
            {loading && (
              <div className='absolute inset-0 flex items-center justify-center'>
                <i
                  aria-hidden
                  aria-label='loader'
                  className={cn('fa-light fa-spinner-scale fa-spin-pulse')}
                />
              </div>
            )}
          </>
          {endAdornment && (
            <i
              aria-hidden
              aria-label='endAdornment'
              className={cn(endAdornment, 'fa-1x')}
            />
          )}
        </div>
      </>
    )

    if (link) {
      const isExternal = link.startsWith('http')
      return isExternal ? (
        <a
          href={link}
          {...elementProps}
          target='_blank'
          rel='noopener noreferrer'
        >
          {content}
        </a>
      ) : (
        <Link
          href={link}
          {...elementProps}
        >
          {content}
        </Link>
      )
    }

    return asChild ? (
      <div {...elementProps}>{content}</div>
    ) : (
      <button
        ref={ref}
        {...elementProps}
        {...props}
      >
        {content}
      </button>
    )
  },
)
Button.displayName = 'Button'
